/**
 * Generic planning "Filters" button + portaled popover shared by the Schedule
 * and Dispatch toolbars. Renders config-driven checkbox toggles and
 * SearchableMultiSelect filters plus a "Clear All Filters" button; all filter
 * state lives in the host module (passed in via props).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Filter } from "../../../../components/icons/svg/outline";
import { SearchableMultiSelect } from "../../../../components/ui";
import useOutsideClick from "../../../../hooks/useOutsideClick";

/**
 * @typedef {Object} PlanningFilterCheckbox
 * @property {string} id Stable key for the checkbox row.
 * @property {string} label Checkbox label text.
 * @property {boolean} checked Current checked state.
 * @property {(checked: boolean) => void} onChange Called with the new checked state.
 * @property {string} [title] Optional tooltip text.
 */

/**
 * @typedef {Object} PlanningFilterSelect
 * @property {string} id Stable key for the select row.
 * @property {string} label Field label above the multi-select.
 * @property {Array<string>} value Selected option values.
 * @property {Array<{ value: string, label: string }>} options Available options.
 * @property {(values: Array<string>) => void} onChange Called with the new selected values.
 * @property {string} [placeholder] Search input placeholder while options remain.
 * @property {string} [emptyPlaceholder] Placeholder when nothing is selected.
 */

/**
 * Filters button and portaled popover panel.
 *
 * The popover renders through a portal to `document.body` with fixed
 * positioning (re-anchored on scroll/resize) so it is never clipped by
 * scrollable/overflow containers in the host page.
 *
 * @param {Object} props
 * @param {Array<PlanningFilterCheckbox>} props.checkboxes Checkbox toggles shown at the top of the panel.
 * @param {Array<PlanningFilterSelect>} props.selects Multi-select filters shown below the checkboxes.
 * @param {boolean} props.isActive Whether any filter is non-default (highlights the button, enables Clear All).
 * @param {() => void} props.onClearAll Resets every filter to its default.
 * @param {string} [props.ariaLabel="Filters"] Accessible label for the popover dialog.
 * @param {boolean} [props.iconOnly=false] Render the trigger as an icon-only button (label moves to a tooltip).
 * @returns {JSX.Element}
 */
export default function PlanningFiltersPanel({
  checkboxes,
  selects,
  isActive,
  onClearAll,
  ariaLabel = "Filters",
  iconOnly = false,
}) {
  const [filtersOpen, setFiltersOpen] = useState(false);
  const panelRef = useRef(null);
  const [panelPosition, setPanelPosition] = useState({ top: 0, right: 0 });

  const closePanel = useCallback(() => setFiltersOpen(false), []);
  // The popover and the multi-select listboxes are portaled, so clicks inside
  // them are not DOM children of the button wrapper; ignore them explicitly.
  useOutsideClick(panelRef, closePanel, {
    ignoreSelector:
      "[data-searchable-multi-select-listbox], [data-planning-filters-panel]",
  });

  /** Anchor the portaled panel to the bottom-right of the Filters button. */
  const updatePanelPosition = useCallback(() => {
    if (!panelRef.current) {
      return;
    }
    const rect = panelRef.current.getBoundingClientRect();
    setPanelPosition({
      top: rect.bottom + 4,
      right: window.innerWidth - rect.right,
    });
  }, []);

  // Keep the portaled panel aligned with the button while open.
  useEffect(() => {
    if (!filtersOpen) {
      return undefined;
    }
    updatePanelPosition();
    window.addEventListener("scroll", updatePanelPosition, true);
    window.addEventListener("resize", updatePanelPosition);
    return () => {
      window.removeEventListener("scroll", updatePanelPosition, true);
      window.removeEventListener("resize", updatePanelPosition);
    };
  }, [filtersOpen, updatePanelPosition]);

  return (
    <div ref={panelRef} className="relative flex h-6 shrink-0 items-center">
      <button
        type="button"
        className={`inline-flex h-6 items-center gap-1.5 rounded-md border text-xs font-medium shadow-sm focus:outline-none focus:ring-2 focus:ring-teal-900 focus:ring-offset-1 ${
          iconOnly ? "px-2" : "px-3"
        } ${
          filtersOpen || isActive
            ? "border-teal-600 bg-teal-600 text-white"
            : "border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
        }`}
        aria-expanded={filtersOpen}
        aria-haspopup="true"
        aria-label="Filters"
        title={iconOnly ? "Filters" : undefined}
        onClick={() => setFiltersOpen((open) => !open)}
      >
        <Filter className="h-4 w-4 shrink-0" aria-hidden />
        {iconOnly ? null : "Filters"}
      </button>

      {filtersOpen
        ? createPortal(
            <div
              data-planning-filters-panel
              className="fixed font-inter z-50 max-h-[80vh] w-72 overflow-y-auto rounded-lg border border-white bg-white p-4 shadow-lg"
              style={{
                top: `${panelPosition.top}px`,
                right: `${panelPosition.right}px`,
              }}
              role="dialog"
              aria-label={ariaLabel}
            >
              {checkboxes.map((checkbox) => (
                <label
                  key={checkbox.id}
                  className="mb-4 flex cursor-pointer items-start gap-2 text-sm text-gray-700"
                  title={checkbox.title}
                >
                  <input
                    type="checkbox"
                    className="mt-0.5"
                    checked={checkbox.checked}
                    onChange={(e) => checkbox.onChange(e.target.checked)}
                  />
                  <span>{checkbox.label}</span>
                </label>
              ))}

              <div className="space-y-3 border-t border-gray-100 pt-3">
                {selects.map((select) => (
                  <SearchableMultiSelect
                    key={select.id}
                    label={select.label}
                    value={select.value}
                    options={select.options}
                    onChange={select.onChange}
                    placeholder={select.placeholder}
                    emptyPlaceholder={select.emptyPlaceholder}
                  />
                ))}
              </div>
              <button
                type="button"
                className="mt-4 w-full rounded-md border border-gray-300 bg-teal-600 px-3 py-0.5 text-sm font-medium text-white shadow-sm hover:bg-teal-900 disabled:cursor-not-allowed disabled:opacity-50"
                onClick={onClearAll}
                disabled={!isActive}
              >
                Clear All Filters
              </button>
            </div>,
            document.body
          )
        : null}
    </div>
  );
}
